import { FC, useEffect, useMemo } from "react";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  STATUS_BADGE_TYPE,
} from "../../../../../../../../components";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { officeAndContactDetailsSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetHrAndBillingContactsMutation } from "../../../../../../../../api/services/client/clientApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import { ROLE_IDS } from "../../../../../../../../helpers/constants";
import {
  isNotEmpty,
  mapDropdownDataWithName,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  keyDetailsSelector,
  projectStatusSelector,
} from "../../../../../../../../redux/slices/project/projectSelector";
import { ProjectTabFormKey } from "../../../../../../modelsAndSchemas/Models";

interface ProjectOfficeAndContactDetailsProps {
  pageId: string;
}

export const ProjectOfficeAndContactDetails: FC<
  ProjectOfficeAndContactDetailsProps
> = ({ pageId }) => {
  const dispatch = useDispatch();
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const projectStatus = useSelector(projectStatusSelector);
  const { control, getValues } = useFormContext();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();

  const { fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const {
    data: localClientRelationshipOwnerData,
    isPending: isRelationshipOwnerFetching,
    isError: isLocalClientRelationshipOwnerError,
    mutateAsync: getLocalClientData,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getHrContacts,
    data: hrContacts,
    isPending: isHrContactsPending,
    isError: isHrContactsError,
  } = useGetHrAndBillingContactsMutation();

  const {
    mutateAsync: getBillingContacts,
    data: billingContacts,
    isPending: isBillingContactsPending,
    isError: isBillingContactsError,
  } = useGetHrAndBillingContactsMutation();

  const {
    data: projectManagerList,
    isPending: isManagerPending,
    isError: isManagerError,
    mutateAsync: getProjectManagerData,
  } = useGetUsersDetailByRoleMutation();

  const {
    data: projectCoordinatorList,
    isPending: isCoordinatorPending,
    isError: isCoordinatorError,
    mutateAsync: getCoordinatorData,
  } = useGetUsersDetailByRoleMutation();

  const isFieldDisabled = useMemo(
    () =>
      isNotEmpty(projectStatus)
        ? projectStatus === STATUS_BADGE_TYPE.active
        : false,
    [projectStatus],
  );

  const isApisLoading =
    isRelationshipOwnerFetching ||
    isHrContactsPending ||
    isBillingContactsPending ||
    isManagerPending ||
    isCoordinatorPending;

  useEffect(() => {
    dispatch(
      toggleLoader({
        otherDetails: {
          isVisible: isApisLoading,

          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ otherDetails: { isVisible: false } }));
    };
  }, [isApisLoading]);

  const isApisError =
    isLocalClientRelationshipOwnerError ||
    isHrContactsError ||
    isBillingContactsError ||
    isCoordinatorError ||
    isManagerError;

  useEffect(() => {
    if (isApisError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isApisError]);

  const clientId = projectKeyDetails?.client?.id;

  useEffect(() => {
    getLocalClientData({ roleIds: [ROLE_IDS.sales] });
    getCoordinatorData({ roleIds: [ROLE_IDS.projectCoOrdinator] });
    getProjectManagerData({ roleIds: [ROLE_IDS.projectManager] });
  }, []);

  useEffect(() => {
    if (isNotEmpty(clientId)) {
      getHrContacts({
        clientId,
        contactType: "hr",
      });
      getBillingContacts({
        clientId,
        contactType: "billing",
      });
    }
  }, [clientId]);

  const getLabel = (key: string) => {
    return fetchLabel(
      officeAndContactDetailsSiteCoreKeys.officeAndContactDetails,
      key,
    );
  };
  return (
    <MuiBox className="form-container project-detail">
      <MuiGrid container data-testid="project-other-detail">
        <MuiGrid
          size={gridSize}
          className={`${gridSizeMediumClass} sales-office-grid`}
        >
          <FormController
            control={control}
            name={ProjectTabFormKey.salesOfficeId}
          >
            <MuiTextField
              label={getLabel(officeAndContactDetailsSiteCoreKeys.salesOffice)}
              value=""
              id={`${pageId}-${ProjectTabFormKey.salesOfficeId}`}
              placeholder={commonLabels.inputPlaceHolder}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProjectTabFormKey.billingOffice}
          >
            <MuiTextField
              label={getLabel(
                officeAndContactDetailsSiteCoreKeys.billingOffice,
              )}
              value=""
              id={`${pageId}-${ProjectTabFormKey.billingOffice}`}
              placeholder={commonLabels.inputPlaceHolder}
              disabled
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
      <MuiGrid container>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProjectTabFormKey.clientRelationShipOwner}
          >
            <MuiSelect
              label={getLabel(
                officeAndContactDetailsSiteCoreKeys.localClientRelationshipOwnerDropDown,
              )}
              options={mapDropdownDataWithName(
                localClientRelationshipOwnerData,
              )}
              visibleDefaultSelectOption
              showSearch
              id={`${pageId}-${ProjectTabFormKey.clientRelationShipOwner}`}
              placeholder={commonLabels.selectPlaceHolder}
              searchPlaceholder={commonLabels.search}
              isMandatory={!isFieldDisabled}
              disabled={isFieldDisabled}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProjectTabFormKey.billingContact}
          >
            <MuiSelect
              label={getLabel(
                officeAndContactDetailsSiteCoreKeys.billingContactDropDown,
              )}
              options={mapDropdownDataWithName(billingContacts)}
              visibleDefaultSelectOption
              showSearch
              id={`${pageId}-${ProjectTabFormKey.billingContact}`}
              placeholder={commonLabels.selectPlaceHolder}
              searchPlaceholder={commonLabels.search}
              isMandatory={!isFieldDisabled}
              disabled={isFieldDisabled}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController control={control} name={ProjectTabFormKey.hrContact}>
            <MuiSelect
              label={getLabel(
                officeAndContactDetailsSiteCoreKeys.hrContactDropDown,
              )}
              options={mapDropdownDataWithName(hrContacts)}
              visibleDefaultSelectOption
              showSearch
              id={`${pageId}-${ProjectTabFormKey.hrContact}`}
              placeholder={commonLabels.selectPlaceHolder}
              searchPlaceholder={commonLabels.search}
              isMandatory={!isFieldDisabled}
              disabled={isFieldDisabled}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProjectTabFormKey.projectManager}
          >
            <MuiMultiSelect
              label={getLabel(
                officeAndContactDetailsSiteCoreKeys.projectManagerDropDown,
              )}
              selectedValues={getValues(ProjectTabFormKey.projectManager)}
              options={mapDropdownDataWithName(projectManagerList)}
              showSearch
              showSelectAllOption
              showChip
              chipId={`${pageId}-${ProjectTabFormKey.projectManager}-chip`}
              dialogTitle={getLabel(
                officeAndContactDetailsSiteCoreKeys.projectManagerDropDown,
              )}
              id={`${pageId}-${ProjectTabFormKey.projectManager}`}
              isMandatory={!isFieldDisabled}
              disabled={isFieldDisabled}
              isChipEditable={!isFieldDisabled}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>

        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <FormController
            control={control}
            name={ProjectTabFormKey.projectCoordinator}
          >
            <MuiMultiSelect
              label={getLabel(
                officeAndContactDetailsSiteCoreKeys.projectCoordinatorDropDown,
              )}
              options={mapDropdownDataWithName(projectCoordinatorList)}
              selectedValues={getValues(ProjectTabFormKey.projectCoordinator)}
              id={`${pageId}-${ProjectTabFormKey.projectCoordinator}`}
              showSearch
              showSelectAllOption
              showChip
              chipId={`${pageId}-${ProjectTabFormKey.projectCoordinator}-chip`}
              dialogTitle={getLabel(
                officeAndContactDetailsSiteCoreKeys.projectCoordinatorDropDown,
              )}
              disabled={isFieldDisabled}
              isChipEditable={!isFieldDisabled}
              selectPlaceHolder={commonLabels.selectPlaceHolder}
              searchPlaceHolder={commonLabels.search}
              selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
              doneText={commonLabels.done}
              allARIAProps={multiSelectARIALabels}
            />
          </FormController>
        </MuiGrid>
      </MuiGrid>
    </MuiBox>
  );
};
